/**
 * RadarChart — SVG-based radar/spider chart for evaluator score visualization.
 * Used at two levels:
 *   1. Run level: shows aggregated evaluator_summary scores
 *   2. Session level: shows per-session evaluator_scores
 *
 * No external chart library dependency — pure SVG.
 *
 * The radar is the primary form and is sized to print a full 20-character
 * evaluator name (`tool_params_accuracy`, ~99px) without clipping: the viewBox
 * is grown outward by a label gutter, because the room around a polygon is
 * fixed by geometry and does NOT grow with `size` — before the gutter, the
 * widest axis offered 32px at every size, so `size=200` and `size=260` cropped
 * identically.
 *
 * Horizontal bars are the fallback for the two cases a radar genuinely cannot
 * label: fewer than 3 axes (no polygon), and too many axes for the names to
 * sit side by side on the circle (arc spacing < label width — roughly 8+ axes
 * at 20 characters). Bars read long names left-aligned at any length, which is
 * the documented form for long-named categories.
 *
 * Abbreviating is deliberately NOT an option here: these names differ by
 * PREFIX (`sql_` / `tool_` / `argument_correctness`), so trimming to the last
 * segment collapses four distinct metrics onto one label.
 */

import { useTranslation } from "react-i18next";

interface RadarChartProps {
  /** Map of dimension name → score (0.0 - 1.0) */
  scores: Record<string, number>;
  /** Chart size in px (width = height) */
  size?: number;
  /** Fill color for the polygon */
  fillColor?: string;
  /** Stroke color for the polygon */
  strokeColor?: string;
  /** Show score values on each axis */
  showValues?: boolean;
  /** Pass threshold line (default 0.7), used for any dimension without its own. */
  threshold?: number;
  /** Per-dimension pass thresholds (e.g. each metric's own threshold for this run).
   *  The dashed line then follows each axis's threshold instead of one ring. */
  thresholds?: Record<string, number>;
  /** Optional title above chart */
  title?: string;
}

/** Axis label type size, shared by the width estimate below. */
const LABEL_FONT_SIZE = 9;

/**
 * Horizontal room added outside the plot box for the side axis labels.
 *
 * The plot itself is unchanged; this only widens the viewBox so a label can
 * extend past it instead of being cropped (the SVG form of sizing a chart
 * container to include its axis band).
 *
 * Sized from the longest real evaluator name: `tool_params_accuracy` is 20
 * characters, ~99px at 9px type, so a centred label needs ~50px to one side.
 * The un-guttered plot left 16px; 56 leaves 72px, which covers ~29 characters.
 *
 * Raising this widens the chart AND loosens `radarLabelsFit`, so the bar
 * fallback fires later. That coupling is intentional — both are the same
 * question, "is there room for the label" — but it means this value cannot be
 * tuned for looks alone without moving the form boundary.
 */
const LABEL_GUTTER_X = 56;

/** Vertical room for the top/bottom axis label, plus its value line. */
const LABEL_GUTTER_Y = 24;

/**
 * Upper-bound width of a rendered label, in px.
 *
 * A measured advance width would need a DOM text node; these labels are
 * lowercase identifiers with underscores at 9px, which run ~4.6px per
 * character, so 5.0 is a deliberate over-estimate — erring high sends a
 * borderline name to the bars, which always render it in full.
 */
export function estimateLabelWidth(name: string, fontSize = LABEL_FONT_SIZE): number {
  return name.length * fontSize * 0.55;
}

/**
 * Whether every label fits around the polygon.
 *
 * Two independent limits, both of which a long name breaks:
 *   - side room: a centred label on the widest axis may not overflow the viewBox
 *   - arc spacing: neighbouring labels may not overlap each other on the circle
 */
export function radarLabelsFit(
  names: string[],
  size: number,
  labelRadius: number,
  gutterX = LABEL_GUTTER_X,
): boolean {
  if (names.length === 0) return true;
  const widest = Math.max(...names.map((n) => estimateLabelWidth(n)));
  const sideRoom = size / 2 + gutterX - labelRadius;
  const arcSpacing = (2 * Math.PI * labelRadius) / names.length;
  return widest / 2 <= sideRoom && widest <= arcSpacing;
}

/** Threshold of one dimension: its own when given, else the chart-wide one. */
export function axisThreshold(
  name: string,
  thresholds: Record<string, number> | undefined,
  fallback: number,
): number {
  const own = thresholds?.[name];
  return typeof own === "number" && Number.isFinite(own) ? own : fallback;
}

/** Colour of a score: pass/fail against the dimension's own threshold when known,
 *  otherwise the fixed 0.7 / 0.5 bands. */
export function scoreColor(score: number, own: number | undefined): string {
  if (typeof own === "number" && Number.isFinite(own)) return score >= own ? "#22c55e" : "#ef4444";
  return score >= 0.7 ? "#22c55e" : score >= 0.5 ? "#eab308" : "#ef4444";
}

/**
 * Horizontal score bars — the form used whenever the radar cannot label itself.
 * Each row carries the full name, the score, and a tick at that metric's own
 * threshold, so no information is lost relative to the polygon.
 */
function ScoreBars({
  entries,
  width,
  threshold,
  thresholds,
  title,
  tip,
}: {
  entries: [string, number][];
  width: number;
  threshold: number;
  thresholds?: Record<string, number>;
  title?: string;
  tip: (name: string, score: number, value: number) => string;
}) {
  return (
    <div style={{ display: "inline-block", textAlign: "left" }} data-testid="score-bars">
      {title && (
        <div
          style={{
            fontSize: "11px",
            fontWeight: 600,
            color: "var(--eval-text-secondary, #94a3b8)",
            marginBottom: "8px",
            textAlign: "center",
          }}
        >
          {title}
        </div>
      )}
      <div style={{ minWidth: "200px", width: `${Math.max(width, 200)}px` }}>
        {entries.map(([name, score]) => {
          const own = axisThreshold(name, thresholds, threshold);
          const color = scoreColor(score, thresholds?.[name]);
          return (
            <div key={name} style={{ marginBottom: "10px" }} data-testid="score-bar-row">
              <title>{tip(name, score, own)}</title>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "baseline",
                  gap: "8px",
                  fontSize: "10px",
                  marginBottom: "3px",
                }}
              >
                {/* No truncation and no ellipsis: the full name is the point of
                    this form. It wraps if the row is narrow. */}
                <span
                  style={{
                    color: "var(--eval-text-secondary, #94a3b8)",
                    overflowWrap: "anywhere",
                  }}
                >
                  {name}
                </span>
                <span style={{ fontWeight: 700, color, flexShrink: 0 }}>{score.toFixed(2)}</span>
              </div>
              <div
                style={{
                  position: "relative",
                  width: "100%",
                  height: "6px",
                  background: "var(--eval-border, #334155)",
                  borderRadius: "3px",
                }}
              >
                <div
                  style={{
                    width: `${Math.min(score * 100, 100)}%`,
                    height: "100%",
                    background: color,
                    borderRadius: "3px",
                    transition: "width 0.3s",
                  }}
                />
                {/* Threshold tick: the bar's reference line, replacing the
                    radar's dashed polygon. */}
                <span
                  data-testid="score-bar-threshold"
                  title={tip(name, score, own)}
                  style={{
                    position: "absolute",
                    top: "-2px",
                    left: `${Math.min(own * 100, 100)}%`,
                    width: "2px",
                    height: "10px",
                    background: "rgba(234, 179, 8, 0.9)",
                    transform: "translateX(-1px)",
                    borderRadius: "1px",
                    cursor: "help",
                  }}
                />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export function RadarChart({
  scores,
  size = 220,
  fillColor = "rgba(99, 102, 241, 0.25)",
  strokeColor = "rgba(99, 102, 241, 0.8)",
  showValues = true,
  threshold = 0.7,
  thresholds,
  title,
}: RadarChartProps) {
  const { t } = useTranslation("eval");
  const entries = Object.entries(scores).filter(([, v]) => v !== null && v !== undefined);

  const cx = size / 2;
  const cy = size / 2;
  const radius = size / 2 - 40;
  const n = entries.length;
  const labelRadius = radius + 24;

  const tip = (name: string, score: number, value: number) =>
    t("continuous.radarScoreTip", {
      name,
      score: score.toFixed(2),
      value: value.toFixed(2),
    });

  // Fewer than 3 axes has no polygon; long names have nowhere to print.
  if (n < 3 || !radarLabelsFit(entries.map(([name]) => name), size, labelRadius)) {
    return (
      <ScoreBars
        entries={entries}
        width={size}
        threshold={threshold}
        thresholds={thresholds}
        title={title}
        tip={tip}
      />
    );
  }

  const angleStep = (2 * Math.PI) / n;

  // Convert polar to cartesian
  const toXY = (angle: number, r: number) => ({
    x: cx + r * Math.sin(angle),
    y: cy - r * Math.cos(angle),
  });

  // Grid rings (0.2, 0.4, 0.6, 0.8, 1.0)
  const rings = [0.2, 0.4, 0.6, 0.8, 1.0];

  // Threshold polygon
  const thresholdPoints = entries.map(([name], i) => {
    const pt = toXY(i * angleStep, radius * Math.min(axisThreshold(name, thresholds, threshold), 1.0));
    return `${pt.x},${pt.y}`;
  }).join(" ");

  // Data polygon
  const dataPoints = entries.map(([, score], i) => {
    const pt = toXY(i * angleStep, radius * Math.min(score, 1.0));
    return `${pt.x},${pt.y}`;
  }).join(" ");

  // The plot keeps its 0..size coordinates; the viewBox is grown outward so the
  // labels have somewhere to go. Rendered width/height match the grown box, so
  // the surrounding layout reserves the label band instead of cropping it.
  const viewW = size + LABEL_GUTTER_X * 2;
  const viewH = size + LABEL_GUTTER_Y * 2;

  return (
    <div style={{ display: "inline-block", textAlign: "center" }}>
      {title && (
        <div style={{ fontSize: "11px", fontWeight: 600, color: "var(--eval-text-secondary, #94a3b8)", marginBottom: "4px" }}>
          {title}
        </div>
      )}
      <svg
        width={viewW}
        height={viewH}
        viewBox={`${-LABEL_GUTTER_X} ${-LABEL_GUTTER_Y} ${viewW} ${viewH}`}
        style={{ overflow: "visible" }}
      >
        {/* Grid rings */}
        {rings.map((r) => (
          <polygon
            key={r}
            points={Array.from({ length: n }, (_, i) => {
              const pt = toXY(i * angleStep, radius * r);
              return `${pt.x},${pt.y}`;
            }).join(" ")}
            fill="none"
            stroke="var(--eval-border, #334155)"
            strokeWidth="0.5"
            opacity={0.6}
          />
        ))}

        {/* Axis lines */}
        {entries.map(([, ], i) => {
          const pt = toXY(i * angleStep, radius);
          return (
            <line
              key={i}
              x1={cx}
              y1={cy}
              x2={pt.x}
              y2={pt.y}
              stroke="var(--eval-border, #334155)"
              strokeWidth="0.5"
              opacity={0.4}
            />
          );
        })}

        {/* Threshold polygon (dashed) */}
        <polygon
          points={thresholdPoints}
          fill="none"
          stroke="rgba(234, 179, 8, 0.5)"
          strokeWidth="1"
          strokeDasharray="4,3"
        />

        {/* Threshold vertices: a small marker, with a wider transparent hover target
            whose native tooltip names the metric and its threshold. */}
        {entries.map(([name], i) => {
          const thr = axisThreshold(name, thresholds, threshold);
          const pt = toXY(i * angleStep, radius * Math.min(thr, 1.0));
          return (
            <g key={`thr-${i}`} data-testid="radar-threshold-point">
              <title>{t("continuous.radarThresholdTip", { name, value: thr.toFixed(2) })}</title>
              <circle cx={pt.x} cy={pt.y} r="2" fill="rgba(234, 179, 8, 0.8)" />
              <circle cx={pt.x} cy={pt.y} r="7" fill="transparent" style={{ cursor: "help" }} />
            </g>
          );
        })}

        {/* Data polygon */}
        <polygon
          points={dataPoints}
          fill={fillColor}
          stroke={strokeColor}
          strokeWidth="2"
        />

        {/* Data points */}
        {entries.map(([name, score], i) => {
          const pt = toXY(i * angleStep, radius * Math.min(score, 1.0));
          const color = scoreColor(score, thresholds?.[name]);
          return (
            <circle
              key={i}
              cx={pt.x}
              cy={pt.y}
              r="3"
              fill={color}
              stroke="var(--eval-bg, #0f172a)"
              strokeWidth="1.5"
              style={{ cursor: "help" }}
              data-testid="radar-score-point"
            >
              <title>
                {tip(name, score, axisThreshold(name, thresholds, threshold))}
              </title>
            </circle>
          );
        })}

        {/* Labels */}
        {entries.map(([name, score], i) => {
          const pt = toXY(i * angleStep, labelRadius);
          return (
            <g key={`label-${i}`}>
              <text
                x={pt.x}
                y={pt.y}
                textAnchor="middle"
                dominantBaseline="middle"
                fontSize={LABEL_FONT_SIZE}
                fill="var(--eval-text-secondary, #94a3b8)"
                fontWeight="500"
              >
                {name}
              </text>
              {showValues && (
                <text
                  x={pt.x}
                  y={pt.y + 11}
                  textAnchor="middle"
                  dominantBaseline="middle"
                  fontSize={LABEL_FONT_SIZE}
                  fill={scoreColor(score, thresholds?.[name])}
                  fontWeight="700"
                >
                  {score.toFixed(2)}
                </text>
              )}
            </g>
          );
        })}
      </svg>
    </div>
  );
}

export default RadarChart;
